"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  INPUT,
  INPUT_MONO,
  LABEL,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  LETTER_SCALE_PRESETS,
  type LetterBand,
  type LetterScalePreset,
  letterFor,
  minPointsFor,
  percentFromPoints,
  validBands,
} from "@/lib/tool-kit/grades/letter-scales";
import { roundTo } from "@/lib/tool-kit/grades/rounding";

/** English strings. A translated content file overrides these through `ui`. */
export const LETTER_GRADE_CALCULATOR_STRINGS = {
  modeLabel: "Enter the score as",
  modePercent: "A percentage",
  modePoints: "Points earned and possible",
  percentLabel: "Percentage",
  percentPlaceholder: "89.5",
  earnedLabel: "Points earned",
  earnedPlaceholder: "179",
  possibleLabel: "Points possible",
  possiblePlaceholder: "200",
  scaleLabel: "Grading scale",
  scalePlusMinus: "Plus/minus — A 93, A− 90, B+ 87 … D− 60",
  scalePlusMinusAPlus: "Plus/minus with A+ from 97",
  scaleTenPoint: "10-point A–F — A 90, B 80, C 70, D 60",
  scaleCustom: "Custom — type your syllabus's cutoffs",
  roundLabel: "Round to the nearest whole percent first (89.5 → 90)",
  results: "Result",
  percentage: "Percentage",
  letterGrade: "Letter grade",
  gpaPoints: "Points on the 4.0 scale",
  roundedNote: "Rounded to {value}% before the lookup.",
  belowScale: "Below your lowest cutoff",
  enterPercent: "Enter a percentage of 0 or more to see the letter grade.",
  enterPoints:
    "Enter the points earned and a points-possible total greater than zero.",
  scaleHeading: "The scale, with the minimum for each letter",
  colLetter: "Letter",
  colMinPercent: "Minimum %",
  colPoints: "4.0 points",
  colMinPoints: "Minimum points of {total}",
  roundingTableNote:
    "Rounding is on, so each minimum allows for a score half a percent under the cutoff rounding up into it.",
  customHeading: "Your cutoffs",
  customLetter: "Letter",
  customMin: "Minimum %",
  customPoints: "4.0 points",
  addBand: "Add a grade",
  removeBand: "Remove",
  removeBandAria: "Remove grade {n}",
  customInvalid:
    "Give at least one grade a letter, a minimum from 0 to 100 and points of 0 or more.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{percent}% = {letter} ({points} on the 4.0 scale)",
} as const;

type Strings = typeof LETTER_GRADE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Mode = "percent" | "points";
type ScaleChoice = LetterScalePreset | "custom";

interface CustomRow {
  id: number;
  letter: string;
  min: string;
  points: string;
}

/** Rows for the editor. Ids are passed in so the first render is the same on server and client. */
function rowsFrom(
  bands: readonly LetterBand[],
  nextId: () => number
): CustomRow[] {
  return bands.map((band) => ({
    id: nextId(),
    letter: band.letter,
    min: String(band.min),
    points: String(band.points),
  }));
}

/** The percentage the reader entered, or null while the inputs cannot give one. */
function readPercent(
  mode: Mode,
  percentRaw: string,
  earnedRaw: string,
  possibleRaw: string
): number | null {
  if (mode === "percent") {
    const value = parseNumberInput(percentRaw);
    return value !== null && value >= 0 ? value : null;
  }
  const earned = parseNumberInput(earnedRaw);
  const possible = parseNumberInput(possibleRaw);
  if (earned === null || possible === null || earned < 0 || possible <= 0) {
    return null;
  }
  return percentFromPoints(earned, possible);
}

/** The figure the scale is read at: the percentage, or its whole-number rounding. */
function lookupValue(
  percent: number | null,
  roundWhole: boolean
): number | null {
  if (percent === null) {
    return null;
  }
  return roundWhole ? roundTo(percent, 0) : percent;
}

function CustomScaleEditor({
  rows,
  onRows,
  nextId,
  strings,
  empty,
}: {
  rows: CustomRow[];
  onRows: (update: (rows: CustomRow[]) => CustomRow[]) => void;
  nextId: () => number;
  strings: Strings;
  empty: boolean;
}) {
  function update(id: number, patch: Partial<CustomRow>) {
    onRows((all) =>
      all.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }
  const field = (
    row: CustomRow,
    key: "letter" | "min" | "points",
    label: string,
    numeric: boolean
  ) => (
    <div className={numeric ? "w-28 space-y-1" : "w-24 space-y-1"}>
      <label className={LABEL} htmlFor={`lgc-${key}-${row.id}`}>
        {label}
      </label>
      <input
        className={numeric ? INPUT_MONO : INPUT}
        id={`lgc-${key}-${row.id}`}
        inputMode={numeric ? "decimal" : undefined}
        onChange={(event) => update(row.id, { [key]: event.target.value })}
        type="text"
        value={row[key]}
      />
    </div>
  );
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.customHeading}</h3>
      {rows.map((row, index) => (
        <div className="flex flex-wrap items-end gap-3" key={row.id}>
          {field(row, "letter", strings.customLetter, false)}
          {field(row, "min", strings.customMin, true)}
          {field(row, "points", strings.customPoints, true)}
          <button
            aria-label={t(strings.removeBandAria, { n: index + 1 })}
            className={BUTTON_SMALL}
            disabled={rows.length <= 1}
            onClick={() => onRows((all) => all.filter((r) => r.id !== row.id))}
            type="button"
          >
            {strings.removeBand}
          </button>
        </div>
      ))}
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          onRows((all) => [
            ...all,
            { id: nextId(), letter: "", min: "", points: "" },
          ])
        }
        type="button"
      >
        {strings.addBand}
      </button>
      {empty ? <Notice>{strings.customInvalid}</Notice> : null}
    </div>
  );
}

function ScaleTable({
  bands,
  current,
  totalPoints,
  roundWhole,
  strings,
  fmt,
}: {
  bands: readonly LetterBand[];
  current: LetterBand | null;
  totalPoints: number | null;
  roundWhole: boolean;
  strings: Strings;
  fmt: Format;
}) {
  const pct = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.scaleHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colLetter}
              </th>
              <th className={TH} scope="col">
                {strings.colMinPercent}
              </th>
              <th className={TH} scope="col">
                {strings.colPoints}
              </th>
              {totalPoints === null ? null : (
                <th className={TH} scope="col">
                  {t(strings.colMinPoints, { total: pct(totalPoints) })}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {bands.map((band) => (
              <tr
                className={
                  band === current ? "bg-primary/10 font-semibold" : undefined
                }
                key={`${band.letter}-${band.min}`}
              >
                <td className={TD}>{band.letter}</td>
                <td className={TD}>{pct(band.min)}</td>
                <td className={TD}>
                  {fmt(band.points, { minimumFractionDigits: 1 })}
                </td>
                {totalPoints === null ? null : (
                  <td className={TD}>
                    {pct(minPointsFor(band.min, totalPoints, roundWhole))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {roundWhole && totalPoints !== null ? (
        <p className="text-muted-foreground text-xs">
          {strings.roundingTableNote}
        </p>
      ) : null}
    </section>
  );
}

function ResultPanel({
  percent,
  looked,
  band,
  prompt,
  strings,
  fmt,
}: {
  percent: number | null;
  looked: number | null;
  band: LetterBand | null;
  prompt: string;
  strings: Strings;
  fmt: Format;
}) {
  const pct = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const points = band ? fmt(band.points, { minimumFractionDigits: 1 }) : "—";
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {percent === null ? (
        <Notice>{prompt}</Notice>
      ) : (
        <>
          <StatGrid>
            <StatCard label={strings.percentage} value={`${pct(percent)}%`} />
            <StatCard
              emphasis
              label={strings.letterGrade}
              value={band ? band.letter : strings.belowScale}
            />
            <StatCard label={strings.gpaPoints} value={points} />
          </StatGrid>
          {looked !== null && looked !== percent ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.roundedNote, { value: pct(looked) })}
            </p>
          ) : null}
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={
              band
                ? t(strings.summary, {
                    percent: pct(percent),
                    letter: band.letter,
                    points,
                  })
                : ""
            }
          />
        </>
      )}
    </section>
  );
}

export default function LetterGradeCalculatorWidget() {
  const strings = useToolUi(LETTER_GRADE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const idCounter = useRef(0);
  const nextId = () => {
    idCounter.current += 1;
    return idCounter.current;
  };
  const [mode, setMode] = useState<Mode>("percent");
  const [percentRaw, setPercentRaw] = useState("89.5");
  const [earnedRaw, setEarnedRaw] = useState("179");
  const [possibleRaw, setPossibleRaw] = useState("200");
  const [scale, setScale] = useState<ScaleChoice>("plus-minus");
  const [roundWhole, setRoundWhole] = useState(false);
  const [customRows, setCustomRows] = useState<CustomRow[]>([]);

  const bands =
    scale === "custom"
      ? validBands(
          customRows.map((row) => ({
            letter: row.letter,
            min: parseNumberInput(row.min),
            points: parseNumberInput(row.points),
          }))
        )
      : [...LETTER_SCALE_PRESETS[scale]];
  const percent = readPercent(mode, percentRaw, earnedRaw, possibleRaw);
  const looked = lookupValue(percent, roundWhole);
  const band = looked === null ? null : letterFor(looked, bands);
  const possible = parseNumberInput(possibleRaw);
  const totalPoints =
    mode === "points" && possible !== null && possible > 0 ? possible : null;

  function changeScale(next: ScaleChoice) {
    if (next === "custom" && scale !== "custom") {
      setCustomRows(rowsFrom(LETTER_SCALE_PRESETS[scale], nextId));
    }
    setScale(next);
  }

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.modeLabel}
          name="lgc-mode"
          onChange={setMode}
          options={[
            { value: "percent", label: strings.modePercent },
            { value: "points", label: strings.modePoints },
          ]}
          value={mode}
        />
        {mode === "percent" ? (
          <TextField
            id="lgc-percent"
            label={strings.percentLabel}
            numeric
            onChange={setPercentRaw}
            placeholder={strings.percentPlaceholder}
            value={percentRaw}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="lgc-earned"
              label={strings.earnedLabel}
              numeric
              onChange={setEarnedRaw}
              placeholder={strings.earnedPlaceholder}
              value={earnedRaw}
            />
            <TextField
              id="lgc-possible"
              label={strings.possibleLabel}
              numeric
              onChange={setPossibleRaw}
              placeholder={strings.possiblePlaceholder}
              value={possibleRaw}
            />
          </div>
        )}
        <SelectField
          id="lgc-scale"
          label={strings.scaleLabel}
          onChange={changeScale}
          options={[
            { value: "plus-minus", label: strings.scalePlusMinus },
            { value: "plus-minus-a-plus", label: strings.scalePlusMinusAPlus },
            { value: "ten-point", label: strings.scaleTenPoint },
            { value: "custom", label: strings.scaleCustom },
          ]}
          value={scale}
        />
        <Checkbox
          checked={roundWhole}
          label={strings.roundLabel}
          onChange={setRoundWhole}
        />
      </div>

      {scale === "custom" ? (
        <CustomScaleEditor
          empty={bands.length === 0}
          nextId={nextId}
          onRows={setCustomRows}
          rows={customRows}
          strings={strings}
        />
      ) : null}

      <ResultPanel
        band={band}
        fmt={fmt}
        looked={looked}
        percent={percent}
        prompt={mode === "percent" ? strings.enterPercent : strings.enterPoints}
        strings={strings}
      />

      {bands.length > 0 ? (
        <ScaleTable
          bands={bands}
          current={band}
          fmt={fmt}
          roundWhole={roundWhole}
          strings={strings}
          totalPoints={totalPoints}
        />
      ) : null}
    </div>
  );
}
